import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Col,
  Row,
  Checkbox,
  Modal,
  message,
  Divider,
} from 'antd';
import { AiOutlineInfoCircle, AiOutlineArrowRight } from 'react-icons/ai';
import { BsPhone } from 'react-icons/bs';
import { MdOutlineEmail } from 'react-icons/md';
import './MembershipLoginForm.less';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import SpinLoader from '@skygroup/shared/components/spin-loader/SpinLoader';
import ModelApiMessage, {
  ApiStatus,
  ApiStatusMessage,
} from '@skygroup/shared/components/crud-helpers/ModelApiMessage';
import { register as registerMembership } from './MembershipApi';
import useMembershipAuthHelpers from '../../../hooks/useMembershipAuthHelpers';
import { OtpMode } from '../../../store/config';

export interface FormValues {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  agree_terms?: boolean;
  newsletter?: boolean;
}

interface RegistrationFormProps {
  onBackToLogin?: (sessionId?: string, autoLogin?: boolean) => void;
  onLoginClick?: () => void;
  isUpdateProfileMode?: boolean;
  handleSubmitAction?: (values: FormValues) => Promise<void>;
  initialValues?: FormValues;
  submitButtonText?: string;
  errorMessageFallback?: string;
}

const columnSize = {
  xs: 20,
  md: 35,
};

const MembershipRegistrationForm: React.FC<RegistrationFormProps> = ({
  onBackToLogin,
  onLoginClick,
  isUpdateProfileMode = false,
  handleSubmitAction,
  initialValues,
  submitButtonText,
  errorMessageFallback,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { translations: t, config } = useSelector(
    (data: RootState) => data.config
  );
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const { membershipLogin } = useMembershipAuthHelpers();

  const membershipConfig = config?.siteConfig?.membership;
  const showNewsletterCheck = membershipConfig?.showNewsletterCheck;
  const otpMode = OtpMode.MEMBERSHIP;

  const [apiStatusMessage, setApiStatusMessage] = useState(
    {} as ApiStatusMessage
  );

  const [isEmailValid, setIsEmailValid] = useState(
    !!initialValues?.email && initialValues?.email?.trim() !== ''
  );
  const [newsletterChecked, setNewsletterChecked] = useState(initialValues?.newsletter ?? false);
  const [termsChecked, setTermsChecked] = useState(false);

  // useEffect(() => {
  //   if (initialValues && isUpdateProfileMode) {
  //     form.setFieldsValue(initialValues);
  //     setNewsletterChecked(!!initialValues.newsletter);
  //     setIsEmailValid(!!(initialValues.email && initialValues.email.trim()));
  //   }
  // }, [initialValues, isUpdateProfileMode, form]);

  const handleAutoLogin = async (phone: string) => {
    try {
      const loginResponse = await membershipLogin('', phone, otpMode);
      const sessionId = loginResponse.data?.session_id;

      if (sessionId) {
        setTimeout(() => {
          onBackToLogin?.(sessionId, true);
        }, 1000);
        return { success: true };
      }
      throw new Error('Session ID not found');
    } catch (autoLoginError: any) {
      setApiStatusMessage({
        status: ApiStatus.warning,
        data: {
          title: t.auth.registrationSuccess,
          message: t.auth.pleaseLogin,
        },
      });
      setTimeout(() => {
        onBackToLogin?.();
      }, 2000);
      return { success: false };
    }
  };

  const handleSubmit = async (values: FormValues) => {
    setLoading(true);
    setApiStatusMessage({} as ApiStatusMessage);

    try {
      if (handleSubmitAction) {
        await handleSubmitAction({
          ...values,
          newsletter: newsletterChecked,
        });
        return;
      }

      await registerMembership({
        phone: values.phone,
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email || undefined,
        newsletter: values.email ? newsletterChecked : false,
      });

      setApiStatusMessage({
        status: ApiStatus.success,
        data: {
          title: t.auth.registrationSuccess,
          message: t.auth.registrationSuccessMessage,
        },
      });

      await handleAutoLogin(values.phone);
    } catch (error: any) {
      setApiStatusMessage({
        status: ApiStatus.error,
        data: {
          message:
            error.message ?? errorMessageFallback ?? t.auth.registrationError,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SpinLoader isLoading={loading} size="large" onTop={true}>
      {!isUpdateProfileMode && (
        <div className="membership-registration-form-wrapper">
          <h2 className="registration-heading">{t.auth.createYourAccount}</h2>
          <p className="registration-description">
            {t.auth.registrationDescription}
          </p>
        </div>
      )}
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={handleSubmit}
        layout="vertical"
        className="membership-login-form membership-registration-form"
      >
        <Row gutter={16} className="registration-name-row">
          <Col xs={24} sm={12}>
            <Form.Item
              label={t.auth.firstName}
              name="first_name"
              rules={[
                { required: true, message: t.auth.firstNamePlaceholderError },
                { min: 2, message: t.auth.nameMinLength },
              ]}
            >
              <Input placeholder={t.auth.firstNamePlaceholder} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={t.auth.lastName}
              name="last_name"
              rules={[
                { required: true, message: t.auth.lastNamePlaceholderError },
                { min: 2, message: t.auth.nameMinLength },
              ]}
            >
              <Input placeholder={t.auth.lastNamePlaceholder} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label={t.auth.mobileNumber}
          name="phone"
          rules={
            isUpdateProfileMode
              ? []
              : [
                  { required: true, message: t.auth.phonePlaceholderError },
                  { pattern: /^[0-9]{9,}$/, message: t.auth.phoneDigits },
                ]
          }
        >
          <Input
            placeholder={t.auth.phonePlaceholder}
            type="text"
            disabled={isUpdateProfileMode}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              form.setFieldsValue({ phone: value });
            }}
            className="phone-input-numeric"
            prefix={<BsPhone className="phone-input-icon" />}
          />
        </Form.Item>
        {!isUpdateProfileMode && (
          <div className="field-helper-text field-helper-text--info">
            <AiOutlineInfoCircle className="field-helper-icon" />
            <span>{t.auth.mobileHelper}</span>
          </div>
        )}
        <Form.Item
          label={t.auth.email}
          name="email"
          rules={[{ type: 'email', message: t.auth.emailInvalid }]}
        >
          <Input
            placeholder={t.auth.emailPlaceholder}
            type="email"
            prefix={<MdOutlineEmail className="email-input-icon" />}
            onChange={(e) => {
              setIsEmailValid(
                !!e.target.value && e.target.value?.trim() !== ''
              );
            }}
          />
        </Form.Item>
        {!isUpdateProfileMode && (
          <div className="field-helper-text field-helper-text--muted">
            <span>{t.auth.emailHelper}</span>
          </div>
        )}
        {showNewsletterCheck && (
          <Form.Item
            name="newsletter"
            // valuePropName="checked"
            initialValue={false}
          >
            <div
              className={'newsletter checkbox-container'}
              onClick={(e) => {
                if (!isEmailValid) {
                  e.preventDefault();
                  message.warning(t.auth.newsletterEmailRequired);
                }
              }}
            >
              <Checkbox
                disabled={!isEmailValid}
                checked={newsletterChecked}
                onChange={(e) => {
                  setNewsletterChecked(e.target.checked);
                  // form.setFieldsValue({ newsletter: e.target.checked });
                }}
              >
                <span
                  className={`checkbox-text ${
                    !isEmailValid ? 'newsletter-disabled' : ''
                  }`}
                >
                  {t.auth.subscribeNewsletter}
                </span>
              </Checkbox>
            </div>
          </Form.Item>
        )}
        {!isUpdateProfileMode && (
          <Form.Item
            name="agree_terms"
            rules={[
              {
                validator: (_, value) =>
                  value || termsChecked
                    ? Promise.resolve()
                    : Promise.reject(new Error(t.auth.agreeToTermsError)),
              },
            ]}
            className="terms-checkbox-item"
          >
            <div className="terms checkbox-container">
              <Checkbox
                className="terms-checkbox"
                checked={termsChecked}
                onChange={(e) => {
                  setTermsChecked(e.target.checked);
                  form.setFieldsValue({ agree_terms: e.target.checked });
                }}
              />
              <span className="checkbox-text">
                <span>*</span>
                <span
                  style={{ margin: '0 4px' }}
                  onClick={() => setTermsChecked((prev) => !prev)}
                >
                  {t.auth.agreeToTerms}
                </span>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsTermsModalOpen(true);
                  }}
                >
                  {t.auth.terms} {t.auth.and} {t.auth.privacyPolicy}
                </a>
              </span>
            </div>
          </Form.Item>
        )}

        <Divider style={{ marginTop: '20px', marginBottom: '10px' }} />

        <Button className="btn-style-5" type="primary" htmlType="submit" block>
          {submitButtonText ?? t.auth.createAccount}
          <AiOutlineArrowRight className="send-code-arrow" />
        </Button>
        {!isUpdateProfileMode && (
          <div className="login-footer-link">
            <span>{t.auth.alreadyHaveAccount}</span>
            <button
              type="button"
              className="login-footer-link-button"
              onClick={onLoginClick ?? (() => onBackToLogin?.())}
            >
              {t.auth.logIn}
            </button>
          </div>
        )}
        <Col style={{ marginTop: '20px' }}>
          <ModelApiMessage apiStatusMessage={apiStatusMessage} t={t.crud} />
        </Col>
      </Form>

      <Modal
        title={`${t.auth.terms} ${t.auth.and} ${t.auth.privacyPolicy}`}
        open={isTermsModalOpen}
        onCancel={() => setIsTermsModalOpen(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            className="btn-style-5"
            onClick={() => setIsTermsModalOpen(false)}
          >
            {t.auth.close}
          </Button>,
        ]}
        className="terms-modal"
        centered
      >
        <div className="terms-modal-content">
          <p>
            Duis aute irure dolor in reprehenderit in voluptate velit esse
            cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat
            cupidatat non proident, sunt in culpa qui officia deserunt mollit
            anim id est laborum.
          </p>
          <p>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua.
          </p>
        </div>
      </Modal>
    </SpinLoader>
  );
};

export default MembershipRegistrationForm;
